import { SelectChangeEvent } from '@mui/material';
import { PhotoEvent, PhotoEventWithPricelistAndPhotos } from 'common-models';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { hasValue } from 'src/lib/Strings';
import { v4 as uuidv4 } from 'uuid';
import {
  useGetAllEventsForAdminQuery,
  useGetAllPricelistsQuery,
  useGetSingleEventWithPhotosQuery,
  useStoreSingleEventMutation,
} from '../../../features/events/eventsApiSlice';

export const useEventEditor = (eventId: string, parentEventId: string | null) => {
  const nav = useNavigate();
  const auth = useAuth();

  // API Queries
  const { data: eventsFromApi = [], isLoading: eventsFromApiIsLoading } = useGetAllEventsForAdminQuery(
    auth.user?.access_token ?? ''
  );
  const { data: photoEventFromApi = undefined } = useGetSingleEventWithPhotosQuery({ eventId: eventId });
  const { data: allPricelists } = useGetAllPricelistsQuery(auth.user?.access_token ?? '');
  const [storeEvent] = useStoreSingleEventMutation();

  // State
  const [photoEvent, setPhotoEvent] = useState(undefined as unknown as PhotoEvent);
  const [startDate, setStartDate] = useState(undefined as unknown as Date);
  const [endDate, setEndDate] = useState(undefined as unknown as Date);
  const [publicEventUrl, setPublicEventUrl] = useState(undefined as unknown as string);
  const [eventIsSubEvent, setEventIsSubEvent] = useState(false);

  // Memoized computed values
  const allEvents = useMemo(() => {
    if (!eventsFromApiIsLoading && eventsFromApi && eventsFromApi.length > 0) {
      return [...eventsFromApi].sort((a, b) => new Date(b.eventStartTime).valueOf() - new Date(a.eventStartTime).valueOf());
    }
    return [];
  }, [eventsFromApi, eventsFromApiIsLoading]);

  // Event handlers
  const onSubmitEventDetails = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const eventToSave = {
      ...photoEvent,
      eventStartTime: startDate ? new Date(startDate) : photoEvent.eventStartTime,
      eventEndTime: endDate ? new Date(endDate) : photoEvent.eventEndTime,
    };

    console.info('Posting event:', eventToSave);
    const result = await storeEvent([eventToSave, auth.user?.access_token ?? '']);
    nav({ pathname: `/admin/events/${photoEvent.id}` });
    console.info(`storeEvent returned: ${JSON.stringify(result)}`);
  };

  const onTitleChanged = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (photoEvent !== undefined) {
      setPhotoEvent((prev) => ({
        ...prev,
        title: event.target.value,
      }));
    }
  };

  const onDatesChanged = async (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start ?? (undefined as unknown as Date));
    setEndDate(end ?? (undefined as unknown as Date));
  };

  const onEventIsSubEventChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEventIsSubEvent(event.target.checked);
  };

  const onParentEventChanged = (event: SelectChangeEvent<string>) => {
    if (photoEvent !== undefined) {
      setEventParentId(eventIsSubEvent ? event.target.value : undefined);
    }
  };

  const onPricelistChanged = (event: SelectChangeEvent<string>) => {
    if (photoEvent !== undefined) {
      setPhotoEvent((prev) => ({
        ...prev,
        priceListId: event.target.value,
      }));
    }
  };

  const setEventParentId = (parentEventId: string | undefined) => {
    if (photoEvent && photoEvent.parentEventId !== parentEventId) {
      setPhotoEvent((prev) => ({
        ...prev,
        parentEventId: parentEventId,
      }));
    }
  };

  const copyEventUrlToClipboard = () => {
    if (publicEventUrl) {
      navigator.clipboard.writeText(`${window.location.protocol}//${window.location.host}${publicEventUrl}`);
    }
  };

  const getEventTitle = (event: PhotoEvent): string => {
    let parentTitles: Array<string> = [];
    let parentId = event.parentEventId;

    while (parentId) {
      const parentEvent = allEvents.find((x) => x.id === parentId);
      if (parentEvent) {
        parentTitles.push(parentEvent.title);
      }
      parentId = parentEvent?.parentEventId;
    }

    return parentTitles.length > 0 ? `${event.title} (${parentTitles.join(', ')})` : event.title;
  };

  const handleCancel = () => {
    nav(-1);
  };

  // Event option handlers
  const onIsPublicChanged = (value: boolean) => {
    setPhotoEvent((prev) => ({ ...prev, isPublic: value }));
  };

  const onIsExternalCopyrightChanged = (value: boolean) => {
    setPhotoEvent((prev) => ({ ...prev, isExternalCopyright: value }));
  };

  const onDisplayFreeOfChargeTextChanged = (value: boolean) => {
    setPhotoEvent((prev) => ({ ...prev, displayFreeOfChargeText: value }));
  };

  const onIsFreeOfChargeChanged = (value: boolean) => {
    setPhotoEvent((prev) => ({ ...prev, isFreeOfCharge: value }));
  };

  // Effects
  useEffect(() => {
    if (photoEvent !== undefined && !eventIsSubEvent) {
      setEventParentId(undefined);
    }
  }, [eventIsSubEvent]);

  useEffect(() => {
    if (photoEvent !== undefined) {
      setPublicEventUrl(`/events/${photoEvent.id}`);
    }
  }, [photoEvent]);

  useEffect(() => {
    const now = new Date();
    if (eventId.toLowerCase() === 'new') {
      setPhotoEvent({
        id: uuidv4(),
        title: '',
        isExternalCopyright: false,
        displayFreeOfChargeText: true,
        eventStartTime: now,
        eventEndTime: now,
        isPublic: true,
        isFreeOfCharge: false,
      });
      setStartDate(now);
      setEndDate(now);
    }
  }, [eventId]);

  useEffect(() => {
    if (photoEventFromApi !== undefined && photoEventFromApi !== null) {
      const tempEvent: PhotoEventWithPricelistAndPhotos = {
        event: {
          eventEndTime: new Date(photoEventFromApi.event.eventEndTime),
          eventStartTime: new Date(photoEventFromApi.event.eventStartTime),
          id: photoEventFromApi.event.id,
          isExternalCopyright: photoEventFromApi.event.isExternalCopyright,
          isFreeOfCharge: photoEventFromApi.event.isFreeOfCharge,
          displayFreeOfChargeText: photoEventFromApi.event.displayFreeOfChargeText,
          isPublic: photoEventFromApi.event.isPublic,
          title: photoEventFromApi.event.title,
          parentEventId: photoEventFromApi.event.parentEventId,
          priceListId: photoEventFromApi.event.priceListId,
          frontImage: photoEventFromApi.event.frontImage,
        },
        photos: photoEventFromApi.photos,
        pricelist: photoEventFromApi.pricelist,
      };

      setStartDate(new Date(tempEvent.event.eventStartTime));
      setEndDate(new Date(tempEvent.event.eventEndTime));
      setPhotoEvent(tempEvent.event);
      setEventIsSubEvent(hasValue(tempEvent.event.parentEventId));
    }
  }, [photoEventFromApi]);

  return {
    // State
    photoEvent,
    startDate,
    endDate,
    publicEventUrl,
    eventIsSubEvent,
    allEvents,
    allPricelists,

    // Handlers
    onSubmitEventDetails,
    onTitleChanged,
    onDatesChanged,
    onEventIsSubEventChanged,
    onParentEventChanged,
    onPricelistChanged,
    copyEventUrlToClipboard,
    getEventTitle,
    handleCancel,
    onIsPublicChanged,
    onIsExternalCopyrightChanged,
    onDisplayFreeOfChargeTextChanged,
    onIsFreeOfChargeChanged,
  };
};
